<template>
  <pf-modal
    :open="open"
    :no-close="noClose"
    v-bind="ouiaProps"
    @update:open="$event ? undefined : cancelClick()"
  >
    <template #description>
      <slot name="description" />
    </template>

    <x-form
      ref="form"
      :id="formId"
      :action="action"
      :additional-data="additionalData"
      :disabled="disabled"
      :encoding="encoding"
      :fake="fake"
      :horizontal="horizontal"
      :method="method"
      :prevent-submit="preventSubmit"
      submit-text=""
      @submit="submit"
    >
      <slot />
    </x-form>

    <template #footer>
      <slot name="footer">
        <pf-button v-if="submitText" type="submit" variant="primary" :form="formId">
          {{ submitText }}
        </pf-button>
        <pf-button v-if="!noClose" variant="link" @click="cancelClick">
          {{ cancelText }}
        </pf-button>
        <slot name="actions" />
      </slot>
    </template>
  </pf-modal>
</template>

<script lang="ts" setup>
import { useId } from 'vue';
import { useOUIAProps } from '@common/ouia';
import { $t as _t } from '@/i18n';
import type { Props as ModalProps } from '@vue-patternfly/core/components/Modal/Modal.vue';
import type { Method } from 'axios';

export interface Props extends /* @vue-ignore */ ModalProps {
  // modal
  open?: boolean;
  noClose?: boolean;
  ouiaId?: string;
  ouiaSafe?: boolean;

  // form
  action?: string;
  additionalData?: Record<string, any>,
  cancelText?: string;
  disabled?: boolean;
  encoding?: 'form' | 'json';
  fake?: boolean;
  horizontal?: boolean | 'sm' | 'md' | 'lg' | 'xl';
  method?: Method;
  preventSubmit?: boolean;
  submitText?: string;
  xhr?: boolean;
  onSubmit?: (e: Event|undefined, responseData?: any) => MaybePromise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  open: true,
  submitText: _t('Procedi'),
  cancelText: _t('Annulla'),
  additionalData: () => ({}),
});

defineSlots<{
  default: (props?: Record<never, never>) => any;
  description: (props?: Record<never, never>) => any;
  footer: (props?: Record<never, never>) => any;
  actions: (props?: Record<never, never>) => any;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'close'): void;
  (e: 'update:open', value: boolean): void;
}>();

const ouiaProps = useOUIAProps(props);
const formId = useId();

async function submit(e: Event|undefined, responseData?: any) {
  await props.onSubmit?.(e, responseData);
  emit('update:open', false);
  emit('close');
}

function cancelClick() {
  emit('cancel');
  emit('update:open', false);
  emit('close');
}
</script>
